import { createContext, useContext, useMemo } from "react";

/**
 * Proposal rendering context.
 *
 * Two rendering modes share the exact same proposal deck component:
 *   - `authed`  (default) — used by /quotes/{id}/print for the internal
 *     Print / Save-as-PDF flow. Images stream via `/api/files/{path}`
 *     with JWT auth.
 *   - `public`  — used by /proposal/{token}. Anyone with the token can
 *     view but not touch the rest of the site. Images stream via
 *     `/api/public/files/{token}/{path}` with no auth required.
 *
 * Components that need image bytes (`ProposalImage`) call
 * `useProposalContext()` to decide which URL builder to use so the
 * consumer never has to prop-drill the share token through 6 nested
 * layout functions.
 */
const ProposalContext = createContext({ mode: "authed", token: null });

export function ProposalProvider({ children, mode = "authed", token = null }) {
  const value = useMemo(() => ({ mode, token }), [mode, token]);
  return <ProposalContext.Provider value={value}>{children}</ProposalContext.Provider>;
}

export function useProposalContext() {
  return useContext(ProposalContext);
}

export function publicFileUrl(token, path) {
  const base = process.env.REACT_APP_BACKEND_URL;
  // Path segments may contain slashes ("uploads/xyz/foo.png") — we
  // rely on `path:path` in the FastAPI route to preserve them, so a
  // plain concat is correct (no encodeURIComponent, which would
  // escape the slashes we WANT preserved).
  return `${base}/api/public/files/${token}/${path}`;
}
